import React, { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  Input,
  Skeleton,
} from '@cargomatic/ui';
import { isEmpty, uniqBy, isString } from 'lodash';
import { humanize, unescapeHTML } from 'underscore.string';
import Pagination from 'components/v2/PaginationV2';
import { Search } from 'lucide-react';
import useDebounce from 'hooks/useDebounce';
import { sluggify } from 'utils/localization/utils';
import { CARRIER_SUBSIDIARY_OPTIONS } from 'constants/subsidiary';
import { DRIVER_CATEGORIZATION_OPTIONS } from 'constants/DriverCategorization';

const getLaneRateIdLink = (meta) => {
  if (!meta?.lanerate_id) return null;
  return (
    <a
      href={`/lane-rates/edit?id=${meta.lanerate_id}`}
      target="_blank"
      rel="noreferrer"
    >
      Lane Rate ID: {meta.lanerate_id}
    </a>
  );
};

function getEventSource(source) {
  if (source._type === 'Driver') {
    if (!source.first_name) return source.email;
    if (source.first_name && source.first_name !== '') {
      return `${source.username} (${source.first_name} ${source.last_name})`;
    }
    return source.username;
  }

  return source.email;
}

const AuditTextToTranslate = [
  [...CARRIER_SUBSIDIARY_OPTIONS, ...DRIVER_CATEGORIZATION_OPTIONS].map(
    (o) => o.value,
  ),
].flat();

function getEventLabel(event, t) {
  const eventKey = sluggify(event);
  let label = t(eventKey);
  if (!label || label === eventKey) {
    label = humanize(event);
  }
  return label;
}

function getDate(date) {
  return moment(date).format('L');
}

function renderNameCell(item, t) {
  const event = getEventLabel(item.event, t);

  if (item?.meta?.referenceNumberType) {
    return (
      <TableCell className="whitespace-nowrap">
        <div className="text-sm" title={event}>
          {event}
          <div style={{ marginTop: 16 }}>
            <div>
              {t('REFERENCE_NUMBER_TYPE')}:{' '}
              {t(sluggify(item?.meta?.referenceNumberType))}
            </div>
            {['before', 'after'].map((el) => {
              if (!(el in item.meta)) return null;
              return (
                <div style={{ marginTop: 4 }} key={el}>
                  {t(el.toUpperCase())}:{' '}
                  {isString(item?.meta?.[el])
                    ? t(sluggify(item.meta[el]))
                    : 'null'}
                </div>
              );
            })}
          </div>
        </div>
      </TableCell>
    );
  }

  let change = '';
  let link = '';
  let description = null;

  if (item.meta && item.meta.event_description) {
    description = item.meta.event_description;
  }

  if (item.meta && item.meta.value) {
    const value = item.meta.value;
    let shipment = '';
    let order = '';
    if (item.meta.shipment && typeof item.meta.shipment === 'string') {
      shipment = item.meta.shipment;
    }
    if (item.meta.order && typeof item.meta.order === 'string') {
      order = item.meta.order;
    }
    if (typeof value === 'string') {
      if (!isEmpty(shipment) || !isEmpty(order)) {
        const valueSplit = value.split(' ');
        const linkId = valueSplit[valueSplit.length - 1];
        valueSplit.pop();
        const path = !isEmpty(shipment) ? 'shipments' : 'orders';
        const id = !isEmpty(shipment) ? shipment : order;

        change = valueSplit.join(' ');
        link = (
          <a href={`/${path}/${id}`} target="_blank" rel="noreferrer noopener">
            {linkId}
          </a>
        );
      } else {
        change = unescapeHTML(unescape(item.meta.value));
      }
    }
  }

  let text;
  let title;
  if (change.length > 50) {
    text = change.substring(0, 40) + '...[truncated]';
    title = change;
  } else if (change && link) {
    text = `{change} {link}`;
    title = text;
  } else {
    text = change;
    title = change;
  }

  if (item?.meta?.contractId && item?.meta?.to) {
    const { start_date: toStartDate = '', end_date: toEndDate = '' } =
      item.meta.to;
    if (item?.meta?.from) {
      const { start_date: fromStartDate = '', end_date: fromEndDate = '' } =
        item.meta.from;
      change = t('START_DATE_END_DATE_CHANGED', {
        startFrom: getDate(fromStartDate),
        startTo: getDate(toStartDate),
        endFrom: getDate(fromEndDate),
        endTo: getDate(toEndDate),
      });
    }
    if (!change) {
      change = t('START_DATE_END_DATE_TIMELINE', {
        startDate: getDate(toStartDate),
        endDate: getDate(toEndDate),
      });
    }
    text = change;
  }

  const laneRateIdLink = getLaneRateIdLink(item.meta);

  return (
    <TableCell className="whitespace-nowrap">
      <div className="text-sm" title={event}>
        {event}
      </div>
      {!isEmpty(change) && (
        <div
          style={{ paddingTop: 20 }}
          className="text-sm text-grey-dark"
          title={title}
        >
          {t('CHANGE_WITH_VALUE', {
            text: AuditTextToTranslate.includes(text)
              ? t(sluggify(text))
              : text,
          })}
        </div>
      )}
      {description && (
        <div
          style={{ paddingTop: 20 }}
          className="text-sm text-grey-dark"
          title={description}
        >
          {description}
        </div>
      )}
      {item.meta?.lanerate_id && laneRateIdLink}
    </TableCell>
  );
}

function AuditHistory({
  audits: auditsProp,
  paginate = true,
  keepDuplicates,
  itemsPerPage = 10,
  onClick,
  isLoading,
  currentPage,
  onSearch,
  onPageChange,
  totalCount: totalCountProp,
}) {
  const { t } = useTranslation();
  const [activePage, setActivePage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const searchTerm = useDebounce(searchInput, 300);

  const isServerSide = typeof onSearch === 'function';

  const onSearchRef = React.useRef(onSearch);
  onSearchRef.current = onSearch;
  const didMountRef = React.useRef(false);

  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    if (onSearchRef.current) {
      onSearchRef.current(searchTerm);
    }
  }, [searchTerm]);

  const processedAudits = useMemo(() => {
    if (!auditsProp) return [];

    let items;

    if (Array.isArray(auditsProp)) {
      items = auditsProp;
      if (!keepDuplicates) {
        items = uniqBy(items, ({ meta, event }) =>
          JSON.stringify({ meta, event }),
        );
      }
    } else if (auditsProp && auditsProp.items) {
      items = auditsProp.items;
    } else {
      return [];
    }

    return items;
  }, [auditsProp, keepDuplicates]);

  const totalCount = totalCountProp ?? processedAudits.length;

  const page = currentPage || activePage;

  // Clamp page when data shrinks
  useEffect(() => {
    if (currentPage != null) return;
    const totalPages = Math.max(Math.ceil(totalCount / itemsPerPage), 1);
    if (activePage > totalPages) {
      setActivePage(totalPages);
    }
  }, [totalCount, itemsPerPage, activePage, currentPage]);

  const activeItems = useMemo(() => {
    if (isServerSide) return processedAudits;
    if (!paginate) return processedAudits;
    return processedAudits.slice(
      (page - 1) * itemsPerPage,
      page * itemsPerPage,
    );
  }, [processedAudits, isServerSide, paginate, page, itemsPerPage]);

  const handlePageClick = (p) => {
    setActivePage(p);
    if (onPageChange) {
      onPageChange(p);
    }
    if (onClick) {
      onClick(p);
    }
  };

  if ((!auditsProp || processedAudits.length === 0) && !isLoading) {
    return (
      <div className="space-y-4">
        {isServerSide && (
          <div className="relative w-full sm:w-80 lg:w-96">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              value={searchInput}
              placeholder={t('SEARCH_BY_EVENT_NAME')}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-8"
            />
          </div>
        )}
        <div>
          {isServerSide && searchInput
            ? t('NO_MATCHING_AUDIT_EVENTS')
            : `${t('AWAITING_AUDIT_EVENTS')}..`}
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto space-y-4">
      {isServerSide && (
        <div className="relative w-full sm:w-80 lg:w-96">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            value={searchInput}
            placeholder={t('SEARCH_BY_EVENT_NAME')}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-8"
          />
        </div>
      )}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="whitespace-nowrap">{t('NAME')}</TableHead>
            <TableHead className="whitespace-nowrap">
              {t('TIMESTAMP')}
            </TableHead>
            <TableHead className="whitespace-nowrap">{t('SOURCE')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading
            ? Array.from({ length: itemsPerPage }, (_, i) => (
                <TableRow key={`skeleton-${i}`}>
                  <TableCell>
                    <Skeleton className="h-4 w-40" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-32" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-28" />
                  </TableCell>
                </TableRow>
              ))
            : activeItems.map((item, index) => {
                const timestamp = moment(item.timestamp).format('LLL');
                const source = getEventSource(item.user);
                return (
                  <TableRow key={index}>
                    {renderNameCell(item, t)}
                    <TableCell className="whitespace-nowrap">
                      <div className="text-sm" title={timestamp}>
                        {timestamp}
                      </div>
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      <div className="text-sm" title={source}>
                        {source}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
        </TableBody>
      </Table>
      {paginate && (
        <Pagination
          perPage={itemsPerPage}
          page={page}
          total={totalCount}
          onPageClick={handlePageClick}
          showCount={false}
        />
      )}
    </div>
  );
}

AuditHistory.propTypes = {
  audits: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.shape({
        meta: PropTypes.shape({
          value: PropTypes.any,
        }),
        event: PropTypes.string,
      }),
    ),
    PropTypes.shape({
      items: PropTypes.array,
      totalCount: PropTypes.number,
    }),
  ]),
  paginate: PropTypes.bool,
  keepDuplicates: PropTypes.bool,
  itemsPerPage: PropTypes.number,
  onClick: PropTypes.func,
  isLoading: PropTypes.bool,
  currentPage: PropTypes.number,
  onSearch: PropTypes.func,
  onPageChange: PropTypes.func,
  totalCount: PropTypes.number,
};

export default AuditHistory;
